import type { GroupBy, PipingSystem } from '../../types/pcfExport.types';

export function buildSystemFilter(
  systems: PipingSystem[],
  groupBy: GroupBy,
  groupFilter?: readonly string[],
): string[] {
  // Parameter grouping is resolved inside the plugin from the parameter value,
  // so the filter carries the selected group labels (matched plugin-side
  // against group display names), not system names. No selection = all groups.
  if (groupBy === 'Parameter') {
    return groupFilter?.length ? [...groupFilter] : [];
  }
  const bySystemType = groupBy === 'SystemTypeName';
  const names: string[] = [];
  const seen = new Set<string>();

  for (const system of systems) {
    const systemName = system.systemName.trim();
    const systemTypeName = String(system.systemTypeName ?? '').trim();
    const name = bySystemType ? systemTypeName || systemName : systemName;
    const normalized = name.toUpperCase();
    if (!name || seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    names.push(name);
  }

  return names;
}

export function selectExportFilesForSystems<T extends { fileName: string }>(
  files: T[],
  systems: Array<{ systemName?: string; systemTypeName?: string }>,
  groupBy: GroupBy,
  groupFilter?: readonly string[],
): T[] {
  // Parameter-grouped files are named after group values, so they match the
  // selected group labels. When nothing matches (the plugin had to sanitize a
  // value into a different file name), every file is kept rather than dropped.
  if (groupBy === 'Parameter') {
    if (!groupFilter?.length) {
      return files;
    }
    const wanted = new Set(groupFilter.map((name) => name.trim().toLowerCase()).filter(Boolean));
    const kept = files.filter((file) => wanted.has(file.fileName.replace(/\.pcf$/i, '').trim().toLowerCase()));
    return kept.length > 0 ? kept : files;
  }
  const bySystemType = groupBy === 'SystemTypeName';
  const normalize = (value: unknown) =>
    String(value ?? '')
      .trim()
      .toLowerCase();
  const keys = new Set(
    systems
      .map((system) => normalize(bySystemType ? system.systemTypeName || system.systemName : system.systemName))
      .filter(Boolean),
  );
  if (keys.size === 0) {
    return files;
  }
  const kept = files.filter((file) => keys.has(normalize(file.fileName.replace(/\.pcf$/i, ''))));
  return kept.length > 0 ? kept : files;
}
